/**
 * mostly copied from Actions old component changing the styling */
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import {
  Button,
  Color,
  Flex,
  Icon,
  IconName,
  SemanticColorName,
} from '@bb/elements-web';

import { ActionModalAppointmentType } from 'contexts/modal/types';
import { useAppointmentActions, useBreakpoint } from 'hooks';
import ShowingDetailsContext from '../../ShowingDetailsV2/ShowingDetailsContext';
import { BORDER_RADIUS } from '../../constants';

type ActionButtonTypes = 'cancel' | 'deny' | 'reschedule' | 'accept';
interface buttonIconTypeMapItem {
  text: string;
  icon: IconName;
  color: SemanticColorName;
  border: string;
  onClick: () => void;
  isLoading?: boolean;
  redirectUrl?: string;
}

const ActionButtons: React.FC = () => {
  const {
    appointment,
    autoAction,
    isPassed,
    listing: { thumbnail },
    tourFunctions,
    fetchAppointment: updateAppointment,
  } = useContext(ShowingDetailsContext);
  const { coopCancel, coopConfirm, coopDeny, loading } = useAppointmentActions({
    appointment: {
      ...(appointment as unknown as ActionModalAppointmentType),
      listing: { ...appointment.listing, thumbnail },
    },
    onConfirm: updateAppointment,
    cancelFromTour: tourFunctions?.cancelFromTour,
  });
  const { md } = useBreakpoint();

  const status =
    appointment.status === 'confirm' && isPassed
      ? 'complete'
      : appointment.status;

  const buttonIconTypeMap: Record<ActionButtonTypes, buttonIconTypeMapItem> = {
    cancel: {
      text: 'Cancel showing',
      icon: 'closeOutlined',
      color: 'danger',
      border: 'none',
      onClick: () => {
        tourFunctions?.closeAppointmentModal();
        coopCancel();
      },
    },
    deny: {
      text: 'Deny',
      icon: 'closeOutlined',
      color: 'danger',
      border: 'none',
      onClick: tourFunctions ? tourFunctions.denySuggestionFromTour : coopDeny,
    },
    reschedule: {
      text: 'Reschedule',
      icon: 'clockCircleOutlined',
      color: 'default',
      border: `1px solid ${Color.BORDER_GREY}`,
      redirectUrl: !tourFunctions?.rescheduleFromTour
        ? `/#/appointments/${appointment.id}/time_change?redirectTo=calendar`
        : undefined,
      onClick: () =>
        tourFunctions?.rescheduleFromTour && tourFunctions.rescheduleFromTour(),
    },
    accept: {
      text: 'Accept',
      icon: 'checkOutlined',
      color: 'primary',
      border: `1px solid ${Color.PRIMARY}`,
      onClick: tourFunctions
        ? tourFunctions.acceptSuggestionFromTour
        : coopConfirm,
      isLoading: loading,
    },
  };
  useEffect(() => {
    switch (status) {
      case 'new':
      case 'attempted':
      case 'time_change':
      case 'confirm':
        if (autoAction === 'cancel') {
          coopCancel();
        }
        break;
      case 'suggested_time_change':
        if (autoAction === 'autoconfirm') {
          coopConfirm();
        }
        if (autoAction === 'deny') {
          coopDeny();
        }
        break;
      default:
        break;
    }
  }, [status, autoAction]);

  const makeButton = (type: ActionButtonTypes) => {
    const { text, icon, color, border, redirectUrl, onClick, isLoading } =
      buttonIconTypeMap[type];
    return (
      <StyledApptActionButton
        $isMobile={!md}
        $border={border}
        color={color}
        dataCy={`showing-${type}-button`}
        href={redirectUrl}
        icon={<Icon icon={icon} color={color} size={12} />}
        key={type}
        loading={isLoading}
        onClick={onClick}
        type="secondary"
      >
        {text}
      </StyledApptActionButton>
    );
  };
  let actionButtons: JSX.Element[] = [];

  switch (status) {
    case 'new':
    case 'attempted':
    case 'time_change':
    case 'confirm':
      actionButtons = [makeButton('cancel'), makeButton('reschedule')];
      break;
    case 'suggested_time_change':
      actionButtons = [makeButton('deny'), makeButton('accept')];
      break;
    default:
      return null;
  }

  return (
    <ActionButtonsContainer gap={16} $isMobile={!md} align="end">
      {actionButtons}
    </ActionButtonsContainer>
  );
};
export default ActionButtons;

const ActionButtonsContainer = styled(Flex)<{ $isMobile?: boolean }>`
  margin: 12px 20px 12px 0px;
  && {
    ${({ $isMobile }) =>
      $isMobile &&
      'width:100%; margin-bottom:20px; justify-content:flex-start;'}
  }
`;
const StyledApptActionButton = styled(Button)<{
  $isMobile: boolean;
  $border: string;
}>`
  && {
    min-width: 160px;
    font-size: ${({ $isMobile }) => ($isMobile ? '16px' : '18px')};
    ${({ $isMobile }) => $isMobile && 'min-width: 50%;'}
    ${({ $border }) => `border: ${$border}; border-radius:${BORDER_RADIUS}px;`}
    &[data-cy='showing-deny-button'], &[data-cy='showing-cancel-button'] {
      &:hover {
        color: ${Color.JANEMBA};
      }
    }
    &[data-cy='showing-reschedule-button'] {
      &:hover {
        color: ${Color.BLACK};
        border-color: ${Color.BLACK};
      }
    }
    .anticon {
      vertical-align: 0;
      ${({ $isMobile }) => $isMobile && 'margin-inline-end:-8px;'}
    }
  }
`;
